import { createContext, useContext, type ReactNode } from 'react'
import { createBrowserMindsetClient, warmUpBrowserMindset } from './browser-mindset-client'
import type { MindsetLicence, MindsetRunToken, MindsetVitalClient } from './mindset-types'

/**
 * A FACTORY rather than a client instance, which is the one structural difference from
 * `FhVitalsClientProvider`.
 *
 * FaceHeart's client is licensed by domain and can be built once at the top of the page. Mindset's
 * is built around an `authenticator` callback that has to carry the patient's scoped access token -
 * a value that does not exist until the date of birth has passed, three screens later. So the seam
 * is the thing that makes a client, and the token is closed over by the flow that owns it.
 */
export type MindsetClientFactory = (options: {
  readonly authenticator: (runToken: MindsetRunToken) => Promise<MindsetLicence>
  /** The camera the flow opened inside the patient's tap (docs/decisions.md D-24). */
  readonly stream?: MediaStream | null
}) => MindsetVitalClient

const MindsetClientContext = createContext<MindsetClientFactory | null>(null)

/**
 * Starts this vendor's runtime loading on the start screen, before the scan that will use it
 * (docs/decisions.md D-63). Separate from the factory because a factory makes a scan's client and this
 * makes none: what it starts, the next client the factory makes adopts.
 */
export type MindsetWarmUp = () => void

const MindsetWarmUpContext = createContext<MindsetWarmUp>(() => {})

/**
 * Production renders with no `factory` prop and gets the real browser adapter; every unit and
 * component test passes a fake, so no test loads the 55 MB vendor bundle, opens a camera or reaches
 * `api.mindsetmedical.com`.
 */
export function MindsetClientProvider({
  factory,
  warmUp,
  children,
}: {
  factory?: MindsetClientFactory
  /**
   * Absent with a fake `factory` means no warm-up at all: the real one loads the 55 MB vendor bundle,
   * which is exactly what passing a fake is for not doing. A test that wants to see it called passes
   * its own.
   */
  warmUp?: MindsetWarmUp
  children: ReactNode
}) {
  return (
    <MindsetClientContext.Provider value={factory ?? createBrowserMindsetClient}>
      <MindsetWarmUpContext.Provider value={warmUp ?? (factory ? () => {} : warmUpBrowserMindset)}>
        {children}
      </MindsetWarmUpContext.Provider>
    </MindsetClientContext.Provider>
  )
}

export function useMindsetClientFactory(): MindsetClientFactory {
  const factory = useContext(MindsetClientContext)
  if (!factory) {
    throw new Error('useMindsetClientFactory() called outside a MindsetClientProvider')
  }
  return factory
}

export function useMindsetWarmUp(): MindsetWarmUp {
  return useContext(MindsetWarmUpContext)
}
